// packages/console/src/components/panels/LiveHardwarePanel.tsx
//
// Encoder-hardware panel for the LIVE console, ported from the "Encoder
// hardware" block of docs/design/ui_kits/aether-live/Transcoder.jsx. It reads
// the REAL LiveHw sample off the live SSE stream (GPU/CPU/VRAM utilisation, plus
// junction temperature, power draw, and encoder session count), not a derived
// sim value.
//
// Honest absence (R10): a GPU-less host omits the GPU fields, so a null meter
// reads 0-width and its numeral is an em-dash. junction / draw / sessions each
// fall to an em-dash when absent.

import { Panel, Read, Meter } from "../Parts";
import type { LiveHw } from "../../api/live";
import { EM_DASH, fmtNum, fmtPct } from "../../lib/format";

export function LiveHardwarePanel({ hw }: { hw: LiveHw | null }) {
  const meters: { label: string; v: number | null; color: string }[] = [
    { label: "GPU · NVENC", v: hw?.gpuUtilPct ?? null, color: "var(--blue-500)" },
    { label: "CPU", v: hw?.cpuUtilPct ?? null, color: "var(--viz-3)" },
    { label: "VRAM", v: hw?.vramPct ?? null, color: "var(--viz-4)" },
  ];
  return (
    <Panel title="Encoder hardware" style={{ flex: "none" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {meters.map((m) => (
          <div key={m.label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span
                style={{
                  font: "500 10px var(--font-sans)",
                  letterSpacing: ".13em",
                  textTransform: "uppercase",
                  color: "var(--fg3)",
                }}
              >
                {m.label}
              </span>
              <span
                style={{
                  font: "400 11px var(--font-mono)",
                  color: "#fff",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtPct(m.v)}
              </span>
            </div>
            <Meter pct={m.v ?? 0} color={m.v !== null ? m.color : "var(--idle)"} />
          </div>
        ))}
        <div style={{ display: "flex", gap: 18, paddingTop: 4 }}>
          <Read value={fmtNum(hw?.junctionC ?? null, 0)} unit="°C" label="Junction" size={16} />
          <Read value={fmtNum(hw?.powerW ?? null, 0)} unit="W" label="Draw" size={16} />
          <Read
            value={hw?.sessions !== null && hw?.sessions !== undefined ? `${hw.sessions}/8` : EM_DASH}
            label="Sessions"
            size={16}
          />
        </div>
      </div>
    </Panel>
  );
}
